import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import AuthLayout from "../components/auth/AuthLayout";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { track, identify, EVT } from "../lib/analytics";

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/api/auth/login", {
        email, password, remember_device: remember,
      });
      if (data.status === "two_factor_required") {
        sessionStorage.setItem("compliche-2fa-challenge", data.challenge_id);
        nav("/two-factor");
        return;
      }
      setUser(data.user);
      identify(data.user);
      track(EVT.user_logged_in, { role: data.user?.role });
      // If user signed up with a pre-selected plan, fire off checkout right after login.
      try {
        const pending = JSON.parse(localStorage.getItem("compliche_pending_plan") || "null");
        if (pending && pending.plan) {
          localStorage.removeItem("compliche_pending_plan");
          track(EVT.plan_upgrade_started, { plan: pending.plan, billing_period: pending.billing_period || "monthly", source: "post_signup" });
          const { data: cs } = await api.post("/api/billing/checkout-session", {
            plan: pending.plan, billing_period: pending.billing_period || "monthly",
            origin_url: window.location.origin,
          });
          if (cs?.url) { window.location.href = cs.url; return; }
        }
      } catch { /* fall through */ }
      try {
        const { data: w } = await api.get("/api/firm/wizard");
        nav(w.completed ? "/app/dashboard" : "/onboarding");
      } catch {
        nav("/app/dashboard");
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your Compliche workspace"
      testid="login-page"
      footer={
        <span>Don't have an account? <Link to="/signup" className="text-indigo2-300 hover:text-white font-medium" data-testid="login-to-signup">Start free trial →</Link></span>
      }
    >
      {err && (
        <div className="mb-4 p-3 rounded-md text-sm flex items-start gap-2" style={{ background: "rgba(239,68,68,0.08)", color: "#991B1B", border: "1px solid #FECACA" }} data-testid="login-error">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{err}</span>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label2">Email address<span className="req">*</span></label>
          <input
            type="email"
            required autoComplete="email"
            className="input2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@firm.co.uk"
            data-testid="login-email"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="label2 m-0">Password<span className="req">*</span></label>
            <Link to="/forgot-password" className="text-indigo2 t-caption font-medium hover:underline" data-testid="forgot-password-link">Forgot?</Link>
          </div>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              required autoComplete="current-password"
              className="input2 pr-12"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate2 hover:text-ink"
              aria-label="Toggle password visibility"
              data-testid="toggle-password"
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <label className="flex items-center gap-2 t-body-sm cursor-pointer select-none">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 accent-indigo2" data-testid="login-remember" />
          Remember this device for 30 days
        </label>

        <button type="submit" className="btn btn-primary btn-block" disabled={loading} data-testid="login-submit">
          {loading ? <><Loader2 size={16} className="animate-spin" />Signing in…</> : "Sign in"}
        </button>
      </form>
    </AuthLayout>
  );
}
